<template>
    <div v-if="mostrar" class="modal-mask" @click="$emit('fechar')">
        <div class="jm margem modal-despesa" @click.stop>
            <div class="modal-header">
                <h3>{{ editando ? 'Editar Despesa' : 'Nova Despesa' }}</h3>
            </div>

            <div class="modal-despesa__corpo">
                <div class="grid-2">
                    <div class="col-2">
                        <label>Projeto *</label>
                        <AutoCompleteSelect
                            v-model="form.projeto_id"
                            :items="projetosOpcoes"
                            label-key="label"
                            value-key="id"
                            :fallback-label="projetoFallbackLabel"
                            placeholder="Buscar projeto em andamento…"
                            @change="onProjetoChange"
                        />
                    </div>
                    <div>
                        <label>Data *</label>
                        <input v-model="form.data" type="date">
                    </div>
                    <div>
                        <label>Valor *</label>
                        <money3 v-model="form.valor" v-bind="configMoney" class="moneyInput"></money3>
                    </div>
                    <div class="col-2">
                        <label>Fornecedor</label>
                        <input v-model="form.fornecedor" type="text" placeholder="Nome do fornecedor">
                    </div>
                    <div>
                        <label>Motivo *</label>
                        <AutoCompleteSelect
                            v-model="form.motivacao_id"
                            :items="motivacoesOpcoes"
                            label-key="nome"
                            value-key="id"
                            :fallback-label="motivacaoFallbackLabel"
                            placeholder="Buscar motivo…"
                        />
                    </div>
                    <div>
                        <label>Categoria de Despesa</label>
                        <AutoCompleteSelect
                            v-model="form.categoria_orcamento_id"
                            :items="categoriasOpcoes"
                            label-key="label"
                            value-key="id"
                            :fallback-label="categoriaFallbackLabel"
                            placeholder="Buscar categoria…"
                        />
                    </div>
                    <div class="col-2">
                        <label>Responsável *</label>
                        <AutoCompleteSelect
                            v-model="form.responsavel_id"
                            :items="usuariosOpcoes"
                            label-key="nomeCompleto"
                            value-key="id"
                            :fallback-label="responsavelFallbackLabel"
                            placeholder="Buscar responsável…"
                        />
                    </div>
                    <div class="col-2">
                        <label>Descrição</label>
                        <textarea v-model="form.descricao" rows="2" placeholder="Descreva a despesa"></textarea>
                    </div>
                    <div class="col-2">
                        <label>Observação</label>
                        <textarea v-model="form.observacao" rows="2" placeholder="Observações adicionais (opcional)"></textarea>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <div class="submit">
                    <button :disabled="salvando" @click="confirmar">
                        {{ salvando ? 'Salvando…' : (editando ? 'Salvar' : 'Cadastrar') }}
                    </button>
                    <button class="acao-secundaria" :disabled="salvando" @click="$emit('fechar')">Cancelar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Money3Component } from 'v-money3'
import AutoCompleteSelect from '@/components/AutoComplete/AutoCompleteSelect.vue'

export default {
    name: 'ModalDespesaProjeto',

    components: {
        money3: Money3Component,
        AutoCompleteSelect,
    },

    props: {
        mostrar: { type: Boolean, default: false },
        projetos: { type: Array, default: () => [] },
        motivacoes: { type: Array, default: () => [] },
        categorias: { type: Array, default: () => [] },
        usuarios: { type: Array, default: () => [] },
        despesaParaEditar: { type: Object, default: null },
    },

    emits: ['fechar', 'salvar'],

    data() {
        return {
            form: this.formVazio(),
            salvando: false,
            configMoney: {
                masked: false,
                prefix: 'R$ ',
                thousands: '.',
                decimal: ',',
                precision: 2,
            },
        }
    },

    computed: {
        editando() {
            return Boolean(this.despesaParaEditar?.id)
        },
        projetosOpcoes() {
            const mapa = new Map()
            for (const p of this.projetos || []) {
                mapa.set(String(p.id), {
                    id: p.id,
                    label: this.formatarLabelProjeto(p),
                })
            }

            const projetoAtual = this.despesaParaEditar?.projeto
            const projetoIdAtual = this.despesaParaEditar?.projeto_id
            if (projetoIdAtual && !mapa.has(String(projetoIdAtual))) {
                mapa.set(String(projetoIdAtual), {
                    id: projetoIdAtual,
                    label: this.formatarLabelProjeto(projetoAtual || { id: projetoIdAtual }),
                })
            }

            return Array.from(mapa.values())
        },
        motivacoesOpcoes() {
            return (this.motivacoes || []).map((m) => ({
                id: m.id,
                nome: m.nome || '',
            }))
        },
        categoriasOpcoes() {
            return (this.categorias || []).map((c) => ({
                id: c.id,
                label: c.label || this.formatarLabelCategoria(c),
            }))
        },
        usuariosOpcoes() {
            return (this.usuarios || []).map((u) => ({
                id: u.id,
                nomeCompleto: u.nomeCompleto || u.name || '',
            }))
        },
        projetoFallbackLabel() {
            return this.formatarLabelProjeto(this.despesaParaEditar?.projeto)
        },
        motivacaoFallbackLabel() {
            return this.despesaParaEditar?.motivacao?.nome || ''
        },
        categoriaFallbackLabel() {
            return this.formatarLabelCategoria(this.despesaParaEditar?.categoria_orcamento)
        },
        responsavelFallbackLabel() {
            return this.despesaParaEditar?.responsavel?.name || ''
        },
    },

    watch: {
        mostrar(visivel) {
            if (!visivel) return
            this.salvando = false
            if (this.despesaParaEditar?.id) {
                this.form = {
                    projeto_id: this.despesaParaEditar.projeto_id ?? '',
                    data: this.despesaParaEditar.data ?? '',
                    fornecedor: this.despesaParaEditar.fornecedor ?? '',
                    descricao: this.despesaParaEditar.descricao ?? '',
                    observacao: this.despesaParaEditar.observacao ?? '',
                    motivacao_id: this.despesaParaEditar.motivacao_id ?? '',
                    categoria_orcamento_id: this.despesaParaEditar.categoria_orcamento_id ?? '',
                    valor: this.despesaParaEditar.valor ?? 0,
                    responsavel_id: this.despesaParaEditar.responsavel_id ?? '',
                }
            } else {
                this.form = this.formVazio()
            }
        },
    },

    methods: {
        formatarLabelProjeto(projeto) {
            if (!projeto) return ''
            const codigo = projeto.codigo || projeto.pcm_codigo
            const nome = projeto.nome || ''
            if (codigo && nome) return `${codigo} - ${nome}`
            return codigo || nome || ''
        },
        formatarLabelCategoria(categoria) {
            if (!categoria) return ''
            const codigo = categoria.codigo || ''
            const descricao = categoria.descricao || ''
            if (codigo && descricao) return `${codigo} - ${descricao}`
            return codigo || descricao || ''
        },
        formVazio() {
            const hoje = new Date()
            const data = [
                hoje.getFullYear(),
                String(hoje.getMonth() + 1).padStart(2, '0'),
                String(hoje.getDate()).padStart(2, '0'),
            ].join('-')
            return {
                projeto_id: '',
                data,
                fornecedor: '',
                descricao: '',
                observacao: '',
                motivacao_id: '',
                categoria_orcamento_id: '',
                valor: 0,
                responsavel_id: '',
            }
        },
        onProjetoChange(projetoId) {
            this.form.projeto_id = projetoId || ''
        },
        confirmar() {
            const valorNumerico = Number(this.form.valor)
            if (!this.form.projeto_id || !this.form.data
                || !this.form.motivacao_id || !this.form.responsavel_id) {
                alert('Preencha todos os campos obrigatórios.')
                return
            }
            if (!valorNumerico || valorNumerico <= 0) {
                alert('Informe um valor maior que zero.')
                return
            }
            this.salvando = true
            this.$emit('salvar', {
                id: this.despesaParaEditar?.id ?? null,
                projeto_id: Number(this.form.projeto_id),
                data: this.form.data,
                fornecedor: (this.form.fornecedor || '').trim() || null,
                descricao: (this.form.descricao || '').trim() || null,
                observacao: (this.form.observacao || '').trim() || null,
                motivacao_id: Number(this.form.motivacao_id),
                categoria_orcamento_id: this.form.categoria_orcamento_id
                    ? Number(this.form.categoria_orcamento_id)
                    : null,
                valor: valorNumerico,
                responsavel_id: Number(this.form.responsavel_id),
            })
        },
        resetSalvando() {
            this.salvando = false
        },
    },
}
</script>

<style scoped>
.modal-despesa {
    position: relative !important;
    display: flex;
    flex-direction: column;
    min-width: min(640px, 92vw);
    max-width: 720px;
    width: min(720px, 94vw);
    max-height: 90vh;
    margin: 1rem auto !important;
    overflow: hidden;
    box-sizing: border-box;
}
.modal-despesa__corpo {
    flex: 1 1 auto;
    min-height: 0;
    max-height: calc(90vh - 9rem);
    overflow-y: auto !important;
    overflow-x: hidden;
    padding-right: 0.25rem;
    -webkit-overflow-scrolling: touch;
}
.modal-header,
.modal-footer {
    flex-shrink: 0;
}
.moneyInput {
    border: 1px solid var(--cor-separador);
    font-size: 16px;
    border-radius: 6px;
    height: 48px;
    padding: 12px 16px;
    width: 100%;
    box-sizing: border-box;
    transition: background-color 0.25s, border 0.25s;
}
.moneyInput:focus {
    outline: none;
    border-color: var(--cor-primaria);
}
</style>
